import {inject, Injectable} from '@angular/core';
import {LoggingService} from "../logger/logging.service";
import {StorageEnum} from "./storage.enum";
import {StorageItem} from "./storage.item";
import {StorageDto} from "./storage.dto";
import {AccStorage} from "./storage.interface";
import {MemoryStorage} from "./storage.memory";
import {Observable, of} from "rxjs";
import {isDefined} from "../../libs/utils/epic.utils";
import {environment} from "../../../environments/environment";

@Injectable({providedIn: 'root'})
export class StorageService {

  public static readonly EXPIRE_UNLIMITED: number = -1;

  private readonly log: LoggingService = inject(LoggingService);

  private readonly enabled: boolean = environment.cache.enabled;
  private readonly cachePrefix = 'cache:';
  /* Der Zwischenspeicher liegt bewusst im Arbeitsspeicher und nicht auf dem Endgerät: Ohne Zugriff auf die
     Endeinrichtung ist § 25 Abs. 1 TDDDG nicht einschlägig und es bedarf keiner Einwilligung. */
  private readonly cacheStorage = StorageEnum.MEMORY;
  private readonly memoryStorage: MemoryStorage = new MemoryStorage();
  private readonly allCaches: StorageEnum[] = [StorageEnum.LOCAL, StorageEnum.SESSION, StorageEnum.MEMORY];
  private defaultStorage = StorageEnum.SESSION;
  private defaultExpire: number = 60 * 60; // 1h

  constructor() {
    this.purgeDeviceCache();
  }

  /**
   * Entfernt Zwischenspeicher-Einträge früherer Fassungen aus der Endeinrichtung.
   *
   * Bis zur Umstellung auf {@link StorageEnum.MEMORY} lag der Zwischenspeicher im
   * `localStorage` bzw. `sessionStorage`. Diese Einträge erreicht danach kein Lesepfad mehr —
   * und damit auch nicht die Ablaufprüfung in {@link getItem}, die sie sonst entfernt hätte.
   * Ohne diese Bereinigung blieben sie unbegrenzt auf dem Gerät liegen, entgegen der Zusage
   * in der Datenschutzerklärung. Läuft bewusst ohne die Prüfung aus {@link isEnabled}:
   * Aufräumen muss auch dann geschehen, wenn der Zwischenspeicher abgeschaltet ist.
   */
  private purgeDeviceCache(): void {
    [StorageEnum.LOCAL, StorageEnum.SESSION].forEach((type) => {
      const storage: AccStorage = this.getStorage(type);
      for (let i = storage.length - 1; i >= 0; i--) {
        const key: string | null = storage.key(i);
        if (isDefined(key) && key.startsWith(this.cachePrefix)) {
          storage.removeItem(key);
        }
      }
    });
  }

  public getItem<T>(key: string, storageType?: StorageEnum): T | null {
    if (this.isEnabled()) {
      this.log.trace('Storage: search item with key "{}" in storage "{}"', [key, storageType]);
      const storage = this.getStorage(storageType);
      const data = storage.getItem(key);
      if (data) {
        const dto: StorageDto<T> = Object.setPrototypeOf(JSON.parse(data), StorageDto.prototype);
        if (dto) {
          if (dto.notExpired()) {
            this.log.trace('Storage: found item with key "{}" in storage "{}": {}', [key, storageType, dto.getValue()]);
            return dto.getValue();
          }
          this.log.trace('Storage: item expired with key "{}" in storage "{}": {}', [key, storageType, dto.getValue()]);
          this.removeItem(key, storageType);
        }
      }
    } else {
      this.log.warn('Storage: cache is disabled');
    }
    return null;
  }

  public setItem<T>(item: StorageItem<T>): void {
    if (this.isEnabled()) {
      const storage = this.getStorage(item.getStorage());
      const key = item.getKey();
      const value = item.getValue();
      const expire = this.buildExpire(item.getExpire());
      this.log.trace('Storage: write item with key "{}" in storage "{}" and expire "{}": {}', [key, item.getStorage(), expire, value]);
      storage.setItem(key, JSON.stringify(new StorageDto<T>(value, expire)));
    } else {
      this.log.warn('Storage: cache is disabled');
    }
  }

  public removeItem(key: string, storageType?: StorageEnum): void {
    if (this.isEnabled()) {
      this.log.trace('Storage: item remove with key "{}" in storage "{}"', [key, storageType]);
      const storage = this.getStorage(storageType);
      storage.removeItem(key);
    } else {
      this.log.warn('Storage: cache is disabled');
    }
  }

  public removeByPrefix(prefix: string, storageType?: StorageEnum): void {
    if (this.isEnabled()) {
      this.log.trace('Storage: items remove by prefix "{}" in storage "{}"', [prefix, storageType]);
      const storage = this.getStorage(storageType);
      for (let i = storage.length - 1; i >= 0; i--) {
        const key = storage.key(i);
        if (key && key.startsWith(prefix)) {
          storage.removeItem(key);
        }
      }
    } else {
      this.log.warn('Storage: cache is disabled');
    }
  }

  public cacheRemove(key: string): void {
    if (this.isEnabled()) {
      const cacheKey = this.cachePrefix + key;
      this.allCaches.forEach((s) => this.removeItem(cacheKey, s));
    } else {
      this.log.warn('Storage: cache is disabled');
    }
  }

  public cacheRemoveByPrefix(prefix: string): void {
    if (this.isEnabled()) {
      const cachePrefix = this.cachePrefix + prefix;
      this.allCaches.forEach((s) => this.removeByPrefix(cachePrefix, s));
    } else {
      this.log.warn('Storage: cache is disabled');
    }
  }

  public cacheUpdate<T>(key: string, fn: () => Observable<T>, expireInSeconds?: number): Observable<T> {
    this.cacheRemove(key);
    return this.cache(key, fn, expireInSeconds);
  }

  /**
   * Either the value is loaded from the cache or the query is executed.
   * e.g., this.storage.cache('key', () => this.repository.getCountries());
   * @param key without 'cache'-Prefix
   * @param fn 'else' function - if value is not found in cache
   * @param expireInSeconds optional
   * @param storage local- (default) or session-storage
   */
  public cache<T>(key: string, fn: () => Observable<T>, expireInSeconds?: number, storage?: StorageEnum): Observable<T> {
    if (this.isEnabled()) {
      const cacheKey = this.cachePrefix + key;
      const cacheStorage = isDefined(storage) ? storage : this.cacheStorage;
      const value: T | null = this.getItem(cacheKey, cacheStorage);
      if (isDefined(value)) {
        return of(value);
      }

      const expire: number = isDefined(expireInSeconds) ? expireInSeconds : this.defaultExpire;
      if (expire > 0) {
        return new Observable<T>((observer) => {
          fn().subscribe({
            next: (res) => {
              this.setItem(new StorageItem(cacheKey, res)
                .inStorage(cacheStorage)
                .withExpire(expire));
              observer.next(res);
            },
            error: (ex) => observer.error(ex),
            complete: () => observer.complete()
          });
        });
      }
    } else {
      this.log.warn('Storage: cache is disabled');
    }
    // Not cacheable
    return fn();
  }

  public cleanAll(): void {
    this.allCaches.forEach((s) => this.getStorage(s).clear());
  }

  private buildExpire(expireInSeconds: number | undefined): number {
    if (expireInSeconds && expireInSeconds === StorageService.EXPIRE_UNLIMITED) {
      return StorageService.EXPIRE_UNLIMITED;
    }
    const expire: number = expireInSeconds ? expireInSeconds : this.defaultExpire;
    return Date.now() + (expire * 1000);
  }

  private getStorage(storage: StorageEnum | undefined): AccStorage {
    switch (storage) {
      case StorageEnum.LOCAL:
        return localStorage;
      case StorageEnum.SESSION:
        return sessionStorage;
      case StorageEnum.MEMORY:
        return this.memoryStorage;
    }
    return this.getStorage(this.defaultStorage);
  }

  private isEnabled(): boolean {
    return this.enabled;
  }
}
